import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ChevronsDown, ChevronsUp, ChevronDown } from 'lucide-react';
import { t } from '@/i18n/translations';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

const CardFoldContext = createContext(null);

export function CardFoldProvider({ children, defaultOpen = true, lang = 'pt' }) {
  const idsRef = useRef(new Set());
  const [openMap, setOpenMap] = useState({});

  const register = useCallback((id) => {
    if (!id) return undefined;
    idsRef.current.add(id);
    return () => {
      idsRef.current.delete(id);
    };
  }, []);

  const isOpen = useCallback(
    (id) => (Object.prototype.hasOwnProperty.call(openMap, id) ? openMap[id] : defaultOpen),
    [openMap, defaultOpen],
  );

  const toggle = useCallback((id) => {
    setOpenMap((prev) => {
      const current = Object.prototype.hasOwnProperty.call(prev, id) ? prev[id] : defaultOpen;
      return { ...prev, [id]: !current };
    });
  }, [defaultOpen]);

  const expandAll = useCallback(() => {
    const next = {};
    idsRef.current.forEach((id) => {
      next[id] = true;
    });
    setOpenMap(next);
  }, []);

  const collapseAll = useCallback(() => {
    const next = {};
    idsRef.current.forEach((id) => {
      next[id] = false;
    });
    setOpenMap(next);
  }, []);

  const value = useMemo(
    () => ({ register, isOpen, toggle, expandAll, collapseAll, defaultOpen, lang }),
    [register, isOpen, toggle, expandAll, collapseAll, defaultOpen, lang],
  );

  return <CardFoldContext.Provider value={value}>{children}</CardFoldContext.Provider>;
}

export function useCardFold(id) {
  const ctx = useContext(CardFoldContext);
  const [localOpen, setLocalOpen] = useState(true);

  useEffect(() => {
    if (!ctx || !id) return undefined;
    return ctx.register(id);
  }, [ctx, id]);

  if (!ctx || !id) {
    return {
      open: localOpen,
      toggle: () => setLocalOpen((v) => !v),
      expandAll: () => setLocalOpen(true),
      collapseAll: () => setLocalOpen(false),
    };
  }

  return {
    open: ctx.isOpen(id),
    toggle: () => ctx.toggle(id),
    expandAll: ctx.expandAll,
    collapseAll: ctx.collapseAll,
  };
}

export function CardFoldToolbar({ lang: langProp, className }) {
  const ctx = useContext(CardFoldContext);
  if (!ctx) return null;
  const lang = langProp || ctx.lang || 'pt';
  return (
    <div className={cn('flex flex-wrap items-center gap-2', className)}>
      <Button type="button" variant="outline" size="sm" onClick={ctx.expandAll}>
        <ChevronsDown className="h-4 w-4" />
        {t('expand_all', lang)}
      </Button>
      <Button type="button" variant="outline" size="sm" onClick={ctx.collapseAll}>
        <ChevronsUp className="h-4 w-4" />
        {t('collapse_all', lang)}
      </Button>
    </div>
  );
}

export function CollapsibleCard({
  id,
  title,
  titleClassName,
  description,
  headerExtra,
  className,
  headerClassName,
  contentClassName,
  children,
}) {
  const { open, toggle } = useCardFold(id);
  const ctx = useContext(CardFoldContext);
  const lang = ctx?.lang || 'pt';

  return (
    <Card className={cn(!open && 'h-fit self-start', className)}>
      <CardHeader
        className={cn(
          'flex-row items-start justify-between space-y-0 gap-3 py-3',
          !open && 'border-b-0 bg-transparent',
          headerClassName,
        )}
      >
        <button
          type="button"
          className="min-w-0 flex-1 rounded-sm text-left transition-colors hover:text-primary"
          onClick={toggle}
          aria-expanded={open}
        >
          <CardTitle className={cn('flex items-center gap-2', titleClassName)}>
            {title}
          </CardTitle>
          {description ? (
            <p className="mt-1.5 text-xs text-muted-foreground">{description}</p>
          ) : null}
        </button>
        <div className="flex shrink-0 items-center gap-2">
          {headerExtra ? (
            <div onClick={(e) => e.stopPropagation()}>{headerExtra}</div>
          ) : null}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 w-7 rounded-full bg-muted/70 p-0"
            aria-label={open ? t('collapse', lang) : t('expand', lang)}
            onClick={toggle}
          >
            <ChevronDown
              className={cn('h-4 w-4 transition-transform duration-200', open ? 'rotate-180' : '')}
            />
          </Button>
        </div>
      </CardHeader>
      {open ? (
        <CardContent className={contentClassName}>{children}</CardContent>
      ) : null}
    </Card>
  );
}
